// Goals — a lista de goals sobre donos nativos, sem estado nosso:
// goal = milestone do GitHub; DoD = issues do milestone; progresso =
// open/closed_issues do payload; custo e cap = team do LiteLLM ligado por
// convenção `team_alias = goal-{milestone_number}`; rodapé (iteração, agente,
// tempo) = spend logs DAS CHAVES do goal, pedidos um `?api_key=` por chave.
// Arquivado = milestone `closed`.

import { repoAtual, gateway, litellm, SourceError, type SpendLog } from "../api";
import { empty, errorBox, esc, head, money, pill, skeleton, toast } from "../ui";

const CSS = `
.goals-card{margin-bottom:12px;padding:14px 16px;cursor:pointer}
.goals-row{display:flex;align-items:center;justify-content:space-between;gap:8px}
.goals-name{font-weight:600;color:inherit;text-decoration:none}
.goals-acts{display:flex;align-items:center;gap:8px}
.goals-icon{background:none;border:none;padding:2px;cursor:pointer;color:inherit;opacity:.65;line-height:0}
.goals-icon:hover{opacity:1}
.goals-icon:disabled{opacity:.3;cursor:default}
.goals-meta{display:flex;justify-content:space-between;font-size:12px;opacity:.8;margin-top:10px}
.goals-bar{height:6px;border-radius:3px;background:rgba(127,127,127,.25);margin-top:6px;overflow:hidden}
.goals-bar>div{height:100%;background:#eab308}
.goals-foot{display:flex;gap:16px;font-size:12px;opacity:.7;margin-top:10px;padding-top:8px;border-top:1px solid rgba(127,127,127,.2)}
`;
if (!document.getElementById("css-goals")) {
  const st = document.createElement("style");
  st.id = "css-goals";
  st.textContent = CSS;
  document.head.append(st);
}

// Glifos do spec: caixa/arquivo (arquivar e restaurar usam o mesmo — f129) e lixeira.
const ICONE = {
  caixa:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>',
  lixeira:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/></svg>',
};

interface Milestone {
  number: number;
  title: string;
  state: "open" | "closed";
  open_issues: number;
  closed_issues: number;
}

interface TeamInfo {
  team_info?: { spend?: number; max_budget?: number | null };
  keys?: Array<{ token?: string }>;
}

type Team = { team_id?: string; team_alias?: string; max_budget?: number };

interface CardData {
  m: Milestone;
  custo: { spend: number; cap: number | null } | null;
  logs: SpendLog[];
}


// ponytail: "atividade recente" = spend log há <30 min; o estado vivo de
// verdade é do runtime do orchestrator, que não tem endpoint — heurística até ele existir.
const RECENTE_MS = 30 * 60000;

const rodando = (c: CardData): boolean => {
  const ultimo = c.logs[c.logs.length - 1]?.startTime;
  return c.m.open_issues > 0 && !!ultimo && Date.now() - Date.parse(ultimo) < RECENTE_MS;
};

/** Milestones da visão + enriquecimento LiteLLM. GitHub caindo derruba a
 *  página (errorBox); LiteLLM caindo só apaga custo e rodapé — o contrato
 *  marca esses campos como derivados/runtime, sem eles o card ainda vale. */
async function carrega(archived: boolean): Promise<CardData[]> {
  const repo = await repoAtual();
  const ms = await gateway<Milestone[]>(
    `github/repos/${repo}/milestones?state=${archived ? "closed" : "open"}&per_page=100`,
  );

  const teams = await litellm.teams().catch(() => [] as Team[]);
  const porAlias = new Map<string, Team>();
  for (const t of teams) if (t.team_alias) porAlias.set(t.team_alias, t);

  return Promise.all(
    ms.map(async (m): Promise<CardData> => {
      const team = porAlias.get(`goal-${m.number}`);
      if (!team?.team_id) return { m, custo: null, logs: [] };

      const info = await gateway<TeamInfo>(`litellm/team/info?team_id=${team.team_id}`).catch(
        () => null,
      );
      // Custo e cap saem do próprio team (`spend`/`max_budget`), que o LiteLLM já
      // soma — exato, e não depende de log nenhum.
      const custo = info?.team_info
        ? { spend: info.team_info.spend ?? 0, cap: info.team_info.max_budget ?? null }
        : null;

      // Rodapé sai do log de CADA chave do goal, um pedido `?api_key=` por chave
      // (dezenas de linhas). Antes vinha de filtrar o log cru do gateway inteiro.
      const chaves = [...new Set((info?.keys ?? []).map((k) => k.token ?? "").filter(Boolean))];
      const logs = (
        await Promise.all(chaves.map((k) => litellm.spendForKey(k).catch(() => [] as SpendLog[])))
      )
        .flat()
        .sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? ""));

      return { m, custo, logs };
    }),
  );
}

// Rodapé do card é em horas no pixel ("<1h elapsed" / "1h elapsed"). O
// duration() de ui.ts é minuto/segundo e serve outras telas — helper local.
const elapsed = (de: string, ate: string | null): string => {
  const h = Math.floor(((ate ? Date.parse(ate) : Date.now()) - Date.parse(de)) / 3600000);
  return h < 1 ? "<1h" : `${h}h`;
};

function card(c: CardData, archived: boolean): string {
  const { m, custo, logs } = c;
  const total = m.open_issues + m.closed_issues;
  const doneGoal = m.open_issues === 0; // derivação do contrato, literal
  const vivo = rodando(c);

  // Badge running exige sinal nos spend logs; goal aberto sem sinal fica sem
  // badge — estado sem dono não ganha rótulo inventado.
  const badge = doneGoal ? pill("done", "ok") : vivo ? pill("running", "run") : "";

  const acoes =
    (archived
      ? `<button class="goals-icon" data-rest="${m.number}" title="Restaurar goal">${ICONE.caixa}</button>`
      : doneGoal
        ? `<button class="goals-icon" data-arch="${m.number}" title="Arquivar goal">${ICONE.caixa}</button>`
        : "") +
    `<button class="goals-icon" data-del="${m.number}" title="Deletar goal">${ICONE.lixeira}</button>`;

  const custoTxt = custo
    ? `${money(custo.spend * 100)}${custo.cap != null ? ` / ${money(custo.cap * 100)}` : ""}`
    : "";

  // Rodapé inteiro deriva do log das chaves do goal (`?api_key=`) — sem logs,
  // sem rodapé. Agregado diário não serve aqui: não traz sessão nem alias.
  let foot = "";
  const inicio = logs[0]?.startTime;
  if (inicio) {
    const ult = logs[logs.length - 1];
    // ponytail: iteração = session_id distinto; entrada sem session_id conta sozinha
    const iter = new Set(logs.map((l) => l.session_id ?? l.startTime)).size;
    const alias = ult?.metadata?.["user_api_key_alias"];
    const agente = vivo && typeof alias === "string" ? alias : "";
    // Fim = início da última chamada: o log por chave não traz `endTime`. Em
    // horas a diferença é invisível, e é medido, não estimado.
    const fim = vivo ? null : (ult?.startTime ?? null);
    foot = `<div class="goals-foot">
        <span>Iteration ${iter}</span>
        ${agente ? `<span>Active: ${esc(agente)}</span>` : ""}
        <span>${elapsed(inicio, fim)} elapsed</span>
      </div>`;
  }

  const pct = total ? Math.round((m.closed_issues / total) * 100) : 0;

  return `<div class="card goals-card" data-goal="${m.number}">
      <div class="goals-row">
        <a class="goals-name" href="#/goals/${m.number}">${esc(m.title)}</a>
        <span class="goals-acts">${badge}${acoes}</span>
      </div>
      <div class="goals-meta">
        <span>${m.closed_issues} of ${total} done</span>
        <span>${esc(custoTxt)}</span>
      </div>
      <div class="goals-bar"><div style="width:${pct}%"></div></div>
      ${foot}
    </div>`;
}

// Criação de goal é tela própria (08-new-goal, rota #/goals/new) — aqui só o
// botão que navega para ela.

async function acaoCard(el: HTMLElement, btn: HTMLButtonElement): Promise<void> {
  const repo = await repoAtual();
  btn.disabled = true;
  try {
    if (btn.dataset.arch) {
      await gateway(`github/repos/${repo}/milestones/${btn.dataset.arch}`, {
        method: "PATCH",
        body: JSON.stringify({ state: "closed" }),
      });
      toast("Goal arquivado");
    } else if (btn.dataset.rest) {
      await gateway(`github/repos/${repo}/milestones/${btn.dataset.rest}`, {
        method: "PATCH",
        body: JSON.stringify({ state: "open" }),
      });
      toast("Goal restaurado");
    } else if (btn.dataset.del) {
      const n = btn.dataset.del;
      // Contrato exige confirmação: deletar milestone deixa as issues da DoD órfãs.
      if (!confirm(`Deletar o goal #${n}? As issues da DoD não são apagadas — ficam órfãs.`)) {
        btn.disabled = false;
        return;
      }
      await gateway(`github/repos/${repo}/milestones/${n}`, { method: "DELETE" });
      const team = (await litellm.teams().catch(() => [] as Team[])).find(
        (x) => x.team_alias === `goal-${n}`,
      );
      if (team?.team_id) {
        await gateway("litellm/team/delete", {
          method: "POST",
          body: JSON.stringify({ team_ids: [team.team_id] }),
        }).catch(() => undefined); // milestone já foi; team órfão não segura o fluxo
      }
      toast("Goal deletado");
    }
    void goals(el);
  } catch (err) {
    toast((err as SourceError).message);
    btn.disabled = false;
  }
}

export async function goals(el: HTMLElement): Promise<void> {
  const archived = location.hash.startsWith("#/goals/archived");

  const header = head({
    title: archived ? "Archived Goals" : "Goals",
    actions: archived
      ? `<a class="btn" href="#/goals">${ICONE.caixa} Back to goals</a>`
      : `<a class="btn" href="#/goals/archived">${ICONE.caixa} Archive</a>
         <a class="btn btn-primary" href="#/goals/new">+ New Goal</a>`,
  });

  el.innerHTML = header + skeleton();
  try {
    const cards = await carrega(archived);
    // Ordenação (running no topo) é client-side, como no contrato.
    const ordem = [...cards].sort((a, b) => Number(rodando(b)) - Number(rodando(a)));
    const lista = ordem.length
      ? ordem.map((c) => card(c, archived)).join("")
      : archived
        ? empty("Nenhum goal arquivado", "Arquive um goal done na lista principal e ele aparece aqui.")
        : empty("Nenhum goal", "Crie um: a definition of done vira issues de um milestone e o loop roda até tudo fechar.");
    el.innerHTML = header + lista;
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }

  // Delegação única (onclick sobrescreve — re-render não acumula listener).
  el.onclick = (e) => {
    const t = e.target as HTMLElement;
    const btn = t.closest<HTMLButtonElement>("[data-arch],[data-rest],[data-del]");
    if (btn) {
      e.preventDefault();
      void acaoCard(el, btn);
      return;
    }
    const c = t.closest<HTMLElement>("[data-goal]");
    if (c?.dataset.goal) location.hash = `#/goals/${c.dataset.goal}`;
  };
}
